import { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useChurch } from '../context/ChurchContext';

// RCC-RAP-001 (§15) — les 4 rapports des Familles Royales. Le Chef de Famille
// n'a que le premier, limité à sa ou ses familles (contrôlé côté serveur).
const REPORTS = [
  { key: 'members',        label: 'Membres d’une Famille' },
  { key: 'summary',        label: 'Synthèse des Familles' },
  { key: 'without-family', label: 'Personnes sans Famille' },
  { key: 'movements',      label: 'Mouvements' },
];

const CARD = {
  background: 'var(--card-bg)', borderRadius: '14px', border: '1px solid var(--border)',
  boxShadow: 'var(--card-shadow)', padding: '18px 20px', marginBottom: '16px',
};
const INPUT = { padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '0.85rem', background: '#fff', color: 'var(--text-primary)' };
const BTN = { padding: '8px 14px', borderRadius: '8px', fontWeight: 700, fontSize: '0.84rem', cursor: 'pointer' };

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const show = (v) => (v === null || v === undefined || v === '') ? '—'
  : (typeof v === 'string' && ISO_DATE.test(v)) ? new Date(v + 'T00:00:00').toLocaleDateString('fr-FR') : v;
const escapeHtml = (v) => String(show(v)).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

export default function FamilyReports() {
  const { user } = useAuth();
  const { activeChurchId } = useChurch();
  const isLeader = user?.role === 'chef_famille';
  const reports = isLeader ? REPORTS.slice(0, 1) : REPORTS;

  const [report, setReport] = useState('members');
  const [families, setFamilies] = useState([]);
  const [familyId, setFamilyId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get('/participants/family-reports/families/')
      .then(res => {
        setFamilies(res.data);
        setFamilyId(prev => prev || (res.data[0] ? String(res.data[0].id) : ''));
      })
      .catch(() => setFamilies([]));
  }, [activeChurchId]);

  const params = useCallback(() => {
    const p = {};
    if (report === 'members') p.family = familyId;
    if (report === 'movements') {
      if (dateFrom) p.date_from = dateFrom;
      if (dateTo) p.date_to = dateTo;
    }
    return p;
  }, [report, familyId, dateFrom, dateTo]);

  useEffect(() => {
    if (report === 'members' && !familyId) { setData(null); return; }
    setLoading(true);
    setError(false);
    api.get(`/participants/family-reports/${report}/`, { params: params(), silentError: true })
      .then(res => setData(res.data))
      .catch(() => { setData(null); setError(true); })
      .finally(() => setLoading(false));
  }, [report, params, familyId, activeChurchId]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/participants/family-reports/${report}/`, { params: { ...params(), export: 'xlsx' }, responseType: 'blob' });
      const match = /filename="([^"]+)"/.exec(res.headers['content-disposition'] || '');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.data);
      a.download = match ? match[1] : 'RCC_FAMILLES.xlsx';
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      // erreurs déjà signalées par l'intercepteur axios
    } finally {
      setExporting(false);
    }
  };

  // Version imprimable : « Imprimer » ou « Enregistrer au format PDF » depuis le navigateur
  const printReport = () => {
    if (!data) return;
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escapeHtml(data.title)}</title>
    <style>
      body { font-family: Arial, sans-serif; padding: 20px; color: #111; }
      h1 { font-size: 17px; margin: 0 0 4px; } .meta { font-size: 12px; color: #555; margin-bottom: 14px; }
      .warn { font-size: 11px; color: #9a3412; margin-bottom: 10px; }
      table { width: 100%; border-collapse: collapse; } th { background: #1e3a8a; color: #fff; padding: 6px 8px; text-align: left; font-size: 11px; }
      td { padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 11px; } tr:nth-child(even) td { background: #f8fafc; }
    </style></head><body>
    <h1>${escapeHtml(data.title)}</h1>
    <div class="meta">${escapeHtml(data.context || '')} · Généré le ${new Date().toLocaleDateString('fr-FR')} · ${data.rows.length} ligne(s)</div>
    <div class="warn">Document confidentiel — usage limité aux besoins autorisés du Royal Centre Chrétien.</div>
    <table><thead><tr>${data.columns.map(c => `<th>${escapeHtml(c)}</th>`).join('')}</tr></thead>
    <tbody>${data.rows.map(r => `<tr>${r.map(v => `<td>${escapeHtml(v)}</td>`).join('')}</tr>`).join('')}</tbody></table>
    </body></html>`;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.print();
  };

  return (
    <div style={{ width: '100%', maxWidth: '1200px' }}>
      <div style={{ marginBottom: '18px' }}>
        <h1 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>
          {isLeader ? 'Ma famille' : 'Rapports des Familles'}
        </h1>
        <p style={{ color: 'var(--text-secondary)', marginTop: '4px', fontSize: '0.9rem' }}>
          {isLeader
            ? 'Les membres de la famille que vous accompagnez, leur assiduité et leurs coordonnées.'
            : 'Membres, synthèse, personnes sans famille et mouvements d’affectation.'}
        </p>
      </div>

      {reports.length > 1 && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
          {reports.map(r => (
            <button key={r.key} onClick={() => setReport(r.key)}
              style={{ ...BTN, border: report === r.key ? 'none' : '1px solid var(--border)', background: report === r.key ? '#1e40af' : '#fff', color: report === r.key ? '#fff' : 'var(--text-secondary)' }}>
              {r.label}
            </button>
          ))}
        </div>
      )}

      <div style={{ ...CARD, display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        {report === 'members' && (
          families.length === 0
            ? <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                {isLeader ? 'Aucune famille ne vous est encore confiée. Contactez votre administrateur.' : 'Aucune famille configurée.'}
              </span>
            : <select value={familyId} onChange={e => setFamilyId(e.target.value)} style={{ ...INPUT, minWidth: '220px' }}>
                {families.map(f => (
                  <option key={f.id} value={f.id}>{f.name}{f.church_name && !isLeader ? ` — ${f.church_name}` : ''}</option>
                ))}
              </select>
        )}
        {report === 'movements' && (
          <>
            <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Du</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={INPUT} />
            <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Au</label>
            <input type="date" value={dateTo} min={dateFrom || undefined} onChange={e => setDateTo(e.target.value)} style={INPUT} />
          </>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
          <button onClick={exportXlsx} disabled={!data || exporting}
            style={{ ...BTN, background: '#eff6ff', color: '#1e40af', border: '1.5px solid #bfdbfe', opacity: !data || exporting ? 0.6 : 1 }}>
            {exporting ? 'Export...' : 'Excel'}
          </button>
          <button onClick={printReport} disabled={!data}
            style={{ ...BTN, background: '#f8fafc', color: '#334155', border: '1.5px solid #e2e8f0', opacity: !data ? 0.6 : 1 }}>
            Imprimer / PDF
          </button>
        </div>
      </div>

      <div style={{ ...CARD, padding: 0, overflowX: 'auto' }}>
        {loading ? (
          <p style={{ padding: '20px', margin: 0, color: 'var(--text-secondary)' }}>Chargement...</p>
        ) : error ? (
          <p style={{ padding: '20px', margin: 0, color: '#b91c1c' }}>Le rapport ne peut pas être chargé pour le moment.</p>
        ) : !data ? null : data.rows.length === 0 ? (
          <p style={{ padding: '20px', margin: 0, color: 'var(--text-secondary)' }}>Aucune donnée pour ce rapport.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {data.columns.map(c => (
                  <th key={c} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '0.74rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row, i) => (
                <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                  {row.map((v, j) => <td key={j} style={{ padding: '9px 12px', color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>{show(v)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {data && <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{data.rows.length} ligne(s){data.context ? ` · ${data.context}` : ''}</p>}
    </div>
  );
}
